import { create } from 'zustand';

/**
 * Die Stationsuhr. Eigener Store, weil sie im Sekundentakt schreibt; läge sie im Hauptstore,
 * würde jeder Tick alles neu bewerten, was daran hängt.
 *
 * ⚠️ **Sie ist ein Hinweis, keine Instanz** (Vorgabe des Nutzers, 2026-09-17). Entscheidend
 * ist der Prüfer: er wählt den Prüfling, und damit **beginnt** die Stationszeit; er schliesst
 * die Station über den Abgabeknopf ab, und damit ist sie **vorbei**. Die Uhr schaltet nichts,
 * sperrt nichts und läuft in nichts hinein — abgelaufene Zeit hält den Prüfer nicht auf.
 *
 * ⚠️ **Gerechnet, nicht gezählt.** Die Uhr merkt sich einen Anker (`Date.now()` minus der schon
 * verstrichenen Zeit) und rechnet den Rest daraus. Ein Zähler, der bei jedem Tick eins abzieht,
 * verliert Sekunden, sobald der WebView im Hintergrund drosselt — und genau das tut er, wenn
 * der Prüfer die Kamera öffnet.
 */

/** Wie lange das Zurücksetzen nach dem Anhalten scharf bleibt. */
export const ARM_MS = 3000;

/** Auswählbare Stationszeiten. */
export const TIMER_PRESETS = [180, 300, 360];

interface TimerState {
  /** Gesamtdauer der Stationszeit in Sekunden. */
  total: number;
  /** Verbleibende Sekunden. */
  left: number;
  running: boolean;
  /**
   * Zählt jedes Ablaufen der Stationszeit mit.
   *
   * ⚠️ **Ein Zähler und kein Merker.** Die Oberfläche hängt daran ihr haptisches Signal; ein
   * `boolean` müsste jemand zurücksetzen, und zwei Abläufe kurz hintereinander — zwei Prüflinge
   * nacheinander — ergäben ein einziges Signal.
   */
  ablaufSignal: number;
  /**
   * Zeitpunkt, der dem Anfang der Stationszeit entspricht (`Date.now() - verstrichen * 1000`);
   * `null`, solange die Uhr steht.
   */
  ankerAt: number | null;
  /** Verstrichene Sekunden — auch bei stehender Uhr gültig. */
  verstrichen: number;
  /**
   * Verbleibende Millisekunden des Fensters, in dem der Uhrknopf „zurücksetzen" bedeutet.
   * `0` heißt: nicht scharf.
   */
  armLeft: number;
  /** Zeitpunkt, an dem das Fenster zugeht. */
  armUntil: number | null;

  /**
   * Sekunden, die die Aufsicht dieser Station gegeben hat (`TIME_ADD`).
   *
   * ⚠️ **Neben `total`, nicht darin.** `total` ist die Dauer der Station; stünde die Zugabe
   * darin, begänne der nächste Prüfling mit der verlängerten Zeit. Jeder Neuanfang
   * (`starte`, `reset`, `setTotal`, `setzeDauer`) setzt sie auf 0.
   */
  zugabe: number;
  /**
   * Die Aufsicht gibt der Station Zeit (Entscheidung des Nutzers, 2026-10-07). Eine
   * abgelaufene Uhr läuft danach wieder, die Zugabe zählt ab jetzt; eine angehaltene bleibt
   * angehalten — beenden kann den Bogen ohnehin nur der Prüfer. Ob die Station überhaupt
   * noch läuft, prüft der Aufrufer (`domain/stationLaeuft.ts`).
   */
  verlaengere: (sekunden: number) => 'laeuft' | 'wieder' | 'angehalten';
  /** Eine Dauer von Hand für **diese** Station; die Uhr steht danach am Anfang. */
  setTotal: (seconds: number) => void;
  /**
   * Die Dauer der Station übernehmen — beim Wechsel der Station oder der Prüfung.
   *
   * ⚠️ **Nur wenn sich wirklich etwas ändert.** Der Aufruf steht in einem Effekt der Kopfzeile
   * und läuft damit bei jedem Rendern; ein blindes Neusetzen stellte die laufende Uhr mitten
   * in der Station auf Anfang zurück.
   */
  setzeDauer: (seconds: number) => void;
  /**
   * Auf Anfang **und** losgelaufen — der Prüfling ist gewählt, die Stationszeit beginnt.
   *
   * Das ist der eine Zeitpunkt, an dem die Uhr von selbst anläuft. Alles andere entscheidet
   * der Prüfer am Knopf.
   */
  starte: () => void;
  /** Die Station ist abgeschlossen: die Uhr hält an, wo sie steht. */
  stoppe: () => void;
  /**
   * Der Knopf im Kopf, mit zwei Bedeutungen:
   * läuft die Uhr → anhalten **und** das Zurücksetzen für drei Sekunden scharf schalten;
   * ist es scharf → auf Anfang setzen; sonst → weiterlaufen.
   *
   * Das Fenster ist der Schutz: Zurücksetzen mitten in einer Station wäre nicht
   * wiederherstellbar, ein eigener Knopf dafür wäre aber zu leicht zu treffen.
   */
  press: () => void;
  /** Auf Anfang, angehalten. */
  reset: () => void;
  pause: () => void;
  /** Vom Taktgeber aufgerufen; schreibt nur, wenn sich etwas Sichtbares ändert. */
  tick: () => void;
}

const VORGABE = 600;

export const useTimerStore = create<TimerState>()((set, get) => ({
  total: VORGABE,
  left: VORGABE,
  running: false,
  ablaufSignal: 0,
  ankerAt: null,
  verstrichen: 0,
  armLeft: 0,
  armUntil: null,
  zugabe: 0,

  setTotal: (seconds) =>
    set({
      total: seconds,
      left: seconds,
      verstrichen: 0,
      running: false,
      ankerAt: null,
      armLeft: 0,
      armUntil: null,
      zugabe: 0,
    }),

  setzeDauer: (seconds) => {
    if (get().total === seconds) return;
    set({
      total: seconds,
      left: seconds,
      verstrichen: 0,
      running: false,
      ankerAt: null,
      armLeft: 0,
      armUntil: null,
      zugabe: 0,
    });
  },

  starte: () =>
    set((s) => ({
      left: s.total,
      verstrichen: 0,
      running: true,
      ankerAt: Date.now(),
      armLeft: 0,
      armUntil: null,
      zugabe: 0,
    })),

  stoppe: () =>
    set((s) => ({
      running: false,
      ankerAt: null,
      verstrichen:
        s.ankerAt != null ? Math.floor((Date.now() - s.ankerAt) / 1000) : s.verstrichen,
      armLeft: 0,
      armUntil: null,
    })),

  press: () => {
    const s = get();
    if (s.running) {
      set({
        running: false,
        ankerAt: null,
        verstrichen:
          s.ankerAt != null ? Math.floor((Date.now() - s.ankerAt) / 1000) : s.verstrichen,
        armLeft: ARM_MS,
        armUntil: Date.now() + ARM_MS,
      });
      return;
    }
    // Bei 0:00 hiesse Weiterlaufen nur: sofort wieder klingeln (Audit 2026-10-11, Fund 20).
    // Der Druck stellt deshalb auf Anfang; der nächste startet.
    if (s.armLeft > 0 || s.left === 0) {
      get().reset();
      return;
    }
    set({ running: true, ankerAt: Date.now() - s.verstrichen * 1000, armLeft: 0, armUntil: null });
  },

  reset: () =>
    set((s) => ({
      left: s.total,
      verstrichen: 0,
      running: false,
      ankerAt: null,
      armLeft: 0,
      armUntil: null,
      zugabe: 0,
    })),

  verlaengere: (sekunden) => {
    const s = get();
    const zugabe = s.zugabe + sekunden;
    if (s.running) {
      set({ zugabe, left: Math.max(0, s.total + zugabe - s.verstrichen) });
      return 'laeuft';
    }
    if (s.left === 0) {
      // Abgelaufen: `verstrichen` steht auf der alten Gesamtzeit (`tick` kappt es), der Anker
      // ab jetzt lässt also genau die neue Zugabe übrig.
      set({
        zugabe,
        left: s.total + zugabe - s.verstrichen,
        running: true,
        ankerAt: Date.now() - s.verstrichen * 1000,
        armLeft: 0,
        armUntil: null,
      });
      return 'wieder';
    }
    set({ zugabe, left: s.left + sekunden });
    return 'angehalten';
  },

  pause: () =>
    set((s) => ({
      running: false,
      ankerAt: null,
      verstrichen:
        s.ankerAt != null ? Math.floor((Date.now() - s.ankerAt) / 1000) : s.verstrichen,
    })),

  tick: () =>
    set((s) => {
      const patch: Partial<TimerState> = {};

      if (s.armUntil != null) {
        const rest = Math.max(0, s.armUntil - Date.now());
        if (rest === 0) {
          patch.armLeft = 0;
          patch.armUntil = null;
        } else if (Math.ceil(rest / 100) !== Math.ceil(s.armLeft / 100)) {
          patch.armLeft = rest;
        }
      }

      if (s.running && s.ankerAt != null) {
        const verstrichen = Math.floor((Date.now() - s.ankerAt) / 1000);
        const gesamt = s.total + s.zugabe;
        const left = Math.max(0, gesamt - verstrichen);

        if (verstrichen !== s.verstrichen) patch.verstrichen = Math.min(verstrichen, gesamt);
        if (left !== s.left) patch.left = left;
        if (left === 0) {
          // ⚠️ **Stehen bleiben, nicht ins Minus zählen.** Die Uhr ist ein Hinweis; ob die
          // Station vorbei ist, entscheidet der Prüfer am Abgabeknopf. Das Signal sagt ihm
          // nur, dass die Zeit um ist, während er den Prüfling ansieht.
          patch.running = false;
          patch.ankerAt = null;
          patch.ablaufSignal = s.ablaufSignal + 1;
        }
      }

      return patch;
    }),
}));

/** `M:SS`, wie im Entwurf. */
export function formatClock(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m + ':' + String(s).padStart(2, '0');
}
